import React from "react";
import Markdown from "markdown-to-jsx";

const elements = [
	"a",
	"abbr",
	"address",
	"area",
	"article",
	"aside",
	"audio",
	"b",
	"base",
	"bdi",
	"bdo",
	"big",
	"blockquote",
	"body",
	"br",
	"button",
	"canvas",
	"caption",
	"cite",
	"code",
	"col",
	"colgroup",
	"data",
	"datalist",
	"dd",
	"del",
	"details",
	"dfn",
	"dialog",
	"div",
	"dl",
	"dt",
	"em",
	"embed",
	"fieldset",
	"figcaption",
	"figure",
	"footer",
	"form",
	"h1",
	"h2",
	"h3",
	"h4",
	"h5",
	"h6",
	"head",
	"header",
	"hgroup",
	"hr",
	"html",
	"i",
	"iframe",
	"img",
	"input",
	"ins",
	"kbd",
	"keygen",
	"label",
	"legend",
	"li",
	"link",
	"main",
	"map",
	"mark",
	"marquee",
	"menu",
	"menuitem",
	"meta",
	"meter",
	"nav",
	"noscript",
	"object",
	"ol",
	"optgroup",
	"option",
	"output",
	"p",
	"param",
	"picture",
	"pre",
	"progress",
	"q",
	"rp",
	"rt",
	"ruby",
	"s",
	"samp",
	"script",
	"section",
	"select",
	"small",
	"source",
	"span",
	"strong",
	"style",
	"sub",
	"summary",
	"sup",
	"table",
	"tbody",
	"td",
	"textarea",
	"tfoot",
	"th",
	"thead",
	"time",
	"title",
	"tr",
	"track",
	"u",
	"ul",
	"var",
	"video",
	"wbr",

	// SVG
	"circle",
	"clipPath",
	"defs",
	"ellipse",
	"foreignObject",
	"g",
	"image",
	"line",
	"linearGradient",
	"marker",
	"mask",
	"path",
	"pattern",
	"polygon",
	"polyline",
	"radialGradient",
	"rect",
	"stop",
	"svg",
	"text",
	"tspan",
];

const allowedElements = ["a", "b", "strong", "em", "u", "code", "del"];

const CustomLink = ({ children, ...props }) => (
	<a {...props} target="_blank" rel="noopener noreferrer">
		{children}
	</a>
);

const inlineCodeFenceRegex = /^\s*```([\s\S]*?)```\s*$/;
const inlineCodeStyle = {
	whiteSpace: "pre-wrap",
	overflowWrap: "anywhere",
	wordBreak: "break-word",
};

// Formatacao do WhatsApp (*negrito*, _italico_, ~riscado~) com a regra DO
// WHATSAPP: o par so formata com o marcador na borda da palavra e sem espaco
// colado por dentro. O markdown formatava o meio da palavra - nome de arquivo
// virava "Detalhamento*do*Agendamento.pdf", com o "do" em italico, e "5*3*2"
// virava negrito. O que nao forma par valido vira caractere literal.
//
// URLs sao guardadas antes e devolvidas no fim: o link e reconhecido inteiro
// pelo markdown, e qualquer troca ou barra de escape entraria no endereco.
const WORD_CHARS = "0-9A-Za-zÀ-ÖØ-öø-ÿ";
const URL_REGEX = /https?:\/\/[^\s]+/g;
const URL_TOKEN_BASE = 0xe100;
const URL_TOKEN_REGEX = /[-]/g;

const MARKERS = [
	{ marker: "*", token: "", markdown: "**" },
	{ marker: "~", token: "", markdown: "~~" },
	{ marker: "_", token: "", markdown: "_" },
].map(item => ({
	...item,
	regex: new RegExp(
		`(^|[^${WORD_CHARS}\\${item.marker}])\\${item.marker}(?=\\S)([^\\${item.marker}\\n]*?\\S)\\${item.marker}(?![${WORD_CHARS}\\${item.marker}])`,
		"g"
	),
}));

export const formatWhatsappMarkers = text => {
	const urls = [];
	let result = text.replace(URL_REGEX, url => {
		urls.push(url);
		return String.fromCharCode(URL_TOKEN_BASE + urls.length - 1);
	});

	MARKERS.forEach(({ token, regex }) => {
		result = result.replace(regex, `$1${token}$2${token}`);
	});

	// Marcador que sobrou nao e formatacao para o WhatsApp: fica literal.
	result = result.replace(/[_*~]/g, "\\$&");

	MARKERS.forEach(({ token, markdown }) => {
		result = result.split(token).join(markdown);
	});

	return result.replace(
		URL_TOKEN_REGEX,
		char => urls[char.charCodeAt(0) - URL_TOKEN_BASE] || char
	);
};

const MarkdownWrapper = ({ children }) => {
	const inlineCodeFenceMatch =
		typeof children === "string" ? children.match(inlineCodeFenceRegex) : null;
	const literalCodeContent = inlineCodeFenceMatch ? inlineCodeFenceMatch[1] : null;
	
	if(children && children.includes('BEGIN:VCARD'))
		//children = "Diga olá ao seu novo contato clicando em *conversar*!";
		children = null;
	
	if(children && children.includes('data:image/'))
		children = null;
	
	if (literalCodeContent === null && typeof children === "string" && children) {
		children = formatWhatsappMarkers(children);
	}

	const options = React.useMemo(() => {
		const markdownOptions = {
			disableParsingRawHTML: true,
			forceInline: true,
			overrides: {
				a: { component: CustomLink },
			},
		};

		elements.forEach(element => {
			if (!allowedElements.includes(element)) {
				markdownOptions.overrides[element] = el => el.children || null;
			}
		});

		return markdownOptions;
	}, []);

	if (!children) return null;
	if (literalCodeContent !== null) {
		return <code style={inlineCodeStyle}>{literalCodeContent}</code>;
	}
	
	return <Markdown options={options}>{children}</Markdown>;
};

export default MarkdownWrapper;
